"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Mail, Globe, Phone, User } from "lucide-react";
import AlexLogo from "@/components/icons/AlexLogo";
import {
  useCreateLeadMutation,
  type LeadCategory,
} from "@/redux/features/lead/leadApi";
import { toast } from "sonner";
import { trackCallClicked } from "@/lib/analytics";

interface CTAVariant {
  id: number;
  headline: string;
  subtitle: string;
  image: string;
  placeholder: string;
  buttonLabel: string;
  category: LeadCategory;
}

const ctaVariants: CTAVariant[] = [
  {
    id: 1,
    headline: "Control the Anxiety",
    subtitle: "Get the free 3-3-3 Grounding Guide sent straight to your inbox.",
    image: "/images/blogs/assets/1 - Vertical.png",
    placeholder: "Enter your email address & receive your guide...",
    buttonLabel: "Get Your Free Guide",
    category: "grounding_guide_333",
  },
  {
    id: 2,
    headline: "Find Your Peace & Reset",
    subtitle:
      "Start your healing journey with the pause & reset blueprint sent to your inbox",
    image: "/images/blogs/assets/2 - Vertical.png",
    placeholder: "Enter your email address & receive your blueprint...",
    buttonLabel: "Get Your Free Blueprint",
    category: "pause_reset_blueprint",
  },
  {
    id: 3,
    headline: "Stay Calm, Avoid Burnout",
    subtitle:
      "Get the free anti burnout weekly planner delivered straight to your inbox",
    image: "/images/blogs/assets/3 - Vertical.png",
    placeholder: "Enter your email address & receive your planner...",
    buttonLabel: "Get Your Free Planner",
    category: "anti_burnout_weekly_planner",
  },
];

export default function SidebarCTA() {
  const [variant, setVariant] = useState<CTAVariant | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Lead API mutation
  const [createLead] = useCreateLeadMutation();

  useEffect(() => {
    let ctaId: number;

    const popupIdStr = sessionStorage.getItem("blog-session-popup-id");
    const ctaIdStr = sessionStorage.getItem("blog-session-cta-id");

    if (popupIdStr !== null && ctaIdStr !== null) {
      ctaId = parseInt(ctaIdStr);
    } else {
      // Pick random popup from [0, 1, 2, 3]
      const ids = [0, 1, 2, 3];
      const popupId = ids[Math.floor(Math.random() * ids.length)];

      if (popupId === 0) {
        // CTA is randomly [1, 2, 3]
        const ctaIds = [1, 2, 3];
        ctaId = ctaIds[Math.floor(Math.random() * ctaIds.length)];
      } else {
        ctaId = popupId;
      }

      sessionStorage.setItem("blog-session-popup-id", popupId.toString());
      sessionStorage.setItem("blog-session-cta-id", ctaId.toString());
    }

    const selectedVariant =
      ctaVariants.find((v) => v.id === ctaId) || ctaVariants[0];
    setVariant(selectedVariant);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || isSubmitting || !variant?.category) return;

    setIsSubmitting(true);
    try {
      await createLead({
        fullName: name,
        email,
        source: "CTA",
        category: variant?.category,
      }).unwrap();
      setName("");
      setEmail("");
      toast.success("Check your inbox!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!variant) {
    return <div className="h-[500px] animate-pulse rounded-2xl bg-gray-200" />;
  }

  return (
    <div className="cta-noise-bg relative w-full overflow-hidden rounded-2xl shadow-lg [@media(max-height:850px)]:pb-6">
      {/* Content */}
      <div className="relative z-10 flex flex-col px-5 pb-0 pt-6">
        {/* Logo and underline */}
        <div className="flex flex-col items-center gap-1">
          <AlexLogo className="h-6 w-auto fill-white text-white" />
          <div className="h-[1.5px] w-[140px] bg-white/70" />
        </div>

        {/* Headline */}
        <h3 className="mt-2 text-center font-virtual text-[48px] leading-none text-[#c5ab5d] [@media(max-height:700px)]:hidden">
          {variant.headline}
        </h3>

        {/* Subtitle */}
        <p className="mt-1 px-2 text-center font-onest text-[12.5px] font-normal leading-snug text-white/95">
          {variant.subtitle}
        </p>

        {/* Multi-Field Form */}
        <form
          onSubmit={handleSubmit}
          className="mt-2.5 flex flex-col gap-1.5 px-1"
        >
          {/* Full Name Input */}
          <div className="flex items-center gap-2 rounded-full bg-white p-1 shadow-md">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#034eb3]">
              <User className="h-4 w-4 text-white" />
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name..."
              className="w-full bg-transparent pr-4 font-onest text-xs text-gray-700 placeholder-gray-400 outline-none"
              required
            />
          </div>

          {/* Email Address Input */}
          <div className="flex items-center gap-2 rounded-full bg-white p-1 shadow-md">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#034eb3]">
              <Mail className="h-4 w-4 text-white" />
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={variant.placeholder}
              className="w-full bg-transparent pr-4 font-onest text-xs text-gray-700 placeholder-gray-400 outline-none"
              required
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-0.5 w-full rounded-full bg-[#c5ab5d] py-2 font-onest text-xs font-semibold text-white shadow-md transition-all hover:bg-[#b0964d] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Submitting..." : variant.buttonLabel}
          </button>
        </form>

        {/* Contact Info */}
        <div className="mt-2.5 flex items-center justify-center gap-3 font-onest text-[11px] font-medium text-white">
          <a
            href="https://www.alexrodriguez.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 transition-colors hover:text-white/80"
          >
            <Globe className="h-3.5 w-3.5 text-white" />
            <span>www.alexrodriguez.com.au</span>
          </a>
          <a
            href="tel:+611300467513"
            className="flex items-center gap-1 transition-colors hover:text-white/80"
            onClick={() => trackCallClicked({ location: "sidebar_cta" })}
          >
            <Phone className="h-3.5 w-3.5 text-white" />
            <span>1300 467 513</span>
          </a>
        </div>

        {/* Bottom Graphic Section */}
        <div className="relative mx-auto mt-2.5 h-[160px] w-full max-w-[280px] [@media(max-height:850px)]:hidden">
          {/* Gold Bar running behind the shoulders */}
          <div className="absolute bottom-6 left-0 right-0 z-0 -mx-5 h-3.5 bg-[#c5ab5d]" />

          {/* Portrait Cutout scaled down slightly, sitting below the contact info */}
          <div className="absolute -left-5 -right-5 bottom-0 z-10 h-[175px]">
            <Image
              src={variant.image}
              alt=""
              fill
              className="object-contain object-bottom"
              sizes="280px"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
